import React from "react";
import { Link, useLocation, useParams, useNavigate } from "react-router-dom";
import { useLang } from "@/lib/LangContext";
import { BUSINESS } from "@/lib/business";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { CheckCircle2, ArrowUpRight, ShieldCheck, Search } from "lucide-react";

export default function Confirmation() {
  const { t, lang } = useLang();
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const appt = location.state?.appt;

  const deviceLabels = {
    phone: t.devices.phone,
    tablet: t.devices.tablet,
    computer: t.devices.computer,
    console: t.devices.console,
  };

  const trackUrl = `${window.location.origin}/track/${id}`;
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&margin=8&data=${encodeURIComponent(trackUrl)}`;

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-6">
            <div className="tick-label" data-testid="confirm-eyebrow">
              {lang === "es" ? "Estado" : "Status"} · {lang === "es" ? "Confirmada" : "Confirmed"}
            </div>
            <h1 className="font-display text-5xl md:text-6xl tracking-tighter leading-[0.95] mt-6">
              {t.confirm.title}
            </h1>
            <div className="mt-6 flex items-center gap-3 text-[#6B1F27]">
              <CheckCircle2 size={22} />
              <span className="tick-label !text-[#6B1F27]">
                {t.confirm.ref}: <span className="font-mono-tight" data-testid="confirm-ref">{id}</span>
              </span>
            </div>
            <p className="text-neutral-600 mt-6 leading-relaxed max-w-md">
              {lang === "es"
                ? "Enviamos la confirmación a tu correo. Un técnico se pondrá en contacto contigo antes de la cita."
                : "We sent a confirmation to your email. A technician will contact you before the appointment."}
            </p>

            <div className="mt-8 hairline bg-white p-5 flex items-start gap-4" data-testid="confirm-warranty">
              <ShieldCheck size={22} className="text-[#6B1F27] mt-1 shrink-0" />
              <div>
                <div className="tick-label">{lang === "es" ? "Garantía incluida" : "Included warranty"}</div>
                <div className="font-display text-lg mt-1">
                  {BUSINESS.warranty_days} {lang === "es" ? "días en cada reparación" : "days on every repair"}
                </div>
                <p className="text-sm text-neutral-600 mt-2">
                  {lang === "es"
                    ? "Escanea el QR para dar seguimiento a tu reparación en cualquier momento."
                    : "Scan the QR anytime to track your repair status."}
                </p>
              </div>
            </div>

            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/" data-testid="confirm-back" className="btn-ghost">
                ← {t.confirm.back}
              </Link>
              <button onClick={() => navigate("/book")} data-testid="confirm-again" className="btn-primary">
                {t.confirm.book_another} <ArrowUpRight size={18} />
              </button>
              <Link to={`/track/${id}`} data-testid="confirm-track" className="btn-ghost">
                <Search size={16} /> {lang === "es" ? "Rastrear" : "Track"}
              </Link>
            </div>
          </div>

          <aside className="lg:col-span-6" data-testid="confirm-details">
            <div className="hairline bg-white p-8">
              <div className="grid grid-cols-1 sm:grid-cols-[220px_1fr] gap-6 items-start">
                <div className="p-3 bg-white hairline w-fit" data-testid="confirm-qr">
                  <img src={qrSrc} alt="Tracking QR" width={220} height={220} />
                </div>
                <div>
                  <div className="tick-label">{lang === "es" ? "Rastrea tu cita" : "Track your repair"}</div>
                  <div className="font-display text-2xl tracking-tight mt-2">
                    {lang === "es" ? "Escanea o visita" : "Scan or visit"}
                  </div>
                  <a
                    href={`/track/${id}`}
                    className="text-[#6B1F27] text-sm mt-2 block break-all font-mono-tight"
                  >
                    /track/{id}
                  </a>
                </div>
              </div>

              {appt && (
                <>
                  <div className="border-t border-neutral-200 mt-8 pt-6">
                    <div className="tick-label mb-4">{lang === "es" ? "Detalles" : "Details"}</div>
                    <dl className="divide-y divide-neutral-200">
                      <Row label={lang === "es" ? "Cliente" : "Customer"} value={appt.customer_name} />
                      <Row label="Email" value={appt.customer_email} />
                      <Row label={lang === "es" ? "Teléfono" : "Phone"} value={appt.customer_phone} />
                      <Row label={t.booking.device_type} value={`${deviceLabels[appt.device_type]} — ${appt.device_brand} ${appt.device_model}`} />
                      <Row label={t.booking.service_type} value={appt.service_type === "at_home" ? t.booking.at_home : t.booking.in_shop} />
                      {appt.address && <Row label={t.booking.address} value={appt.address} />}
                      <Row label={t.booking.date} value={`${appt.appointment_date} · ${appt.appointment_time}`} />
                      <Row label={t.booking.issue} value={appt.issue_description} />
                    </dl>
                  </div>
                </>
              )}
            </div>
          </aside>
        </div>
      </section>
      <Footer />
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="py-3 flex items-start justify-between gap-6">
      <dt className="tick-label whitespace-nowrap">{label}</dt>
      <dd className="text-right text-sm">{value}</dd>
    </div>
  );
}
